import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import Card from '@/components/common/Card'
import Textarea from '@/components/common/Textarea'
import Button from '@/components/common/Button'
import DispatchEditor from '@/components/dispatch/DispatchEditor'

function DraftNotes({ team, restoredFormState }) {
  const navigate = useNavigate()
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [draft, setDraft] = useState(null)
  const [draftLoading, setDraftLoading] = useState(false)
  const [draftSaving, setDraftSaving] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [editingContent, setEditingContent] = useState('')
  const [expandedNotes, setExpandedNotes] = useState({})
  const [messageType, setMessageType] = useState('weekly') // 'weekly' or 'general'
  const [includeSchedule, setIncludeSchedule] = useState(true)
  const [specialInstructions, setSpecialInstructions] = useState('')

  const hasNotes = useMemo(() => notes.length > 0, [notes])
  const hasContent = useMemo(() => hasNotes || draft || specialInstructions.trim(), [hasNotes, draft, specialInstructions])

  // Restore form state when returning from review page
  useEffect(() => {
    if (restoredFormState) {
      setMessageType(restoredFormState.messageType || 'weekly')
      setIncludeSchedule(restoredFormState.includeSchedule !== undefined ? restoredFormState.includeSchedule : true)
      setSpecialInstructions(restoredFormState.specialInstructions || '')
    }
  }, [restoredFormState])

  useEffect(() => {
    fetchNotes()
    fetchDraft()
  }, [team.id])

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('draft_notes')
        .select('*')
        .eq('team_id', team.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setNotes(data || [])
    } catch (err) {
      console.error('Error fetching notes:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDraft = async () => {
    setDraftLoading(true)
    try {
      const { data, error } = await supabase
        .from('dispatch_drafts')
        .select('*')
        .eq('team_id', team.id)
        .maybeSingle()

      if (error) throw error
      setDraft(data || null)
    } catch (err) {
      console.error('Error fetching dispatch draft:', err)
      toast.error('Unable to load current draft')
    } finally {
      setDraftLoading(false)
    }
  }

  const handleSaveDraft = async (updates) => {
    setDraftSaving(true)
    try {
      const payload = {
        team_id: team.id,
        subject: updates.subject ?? draft?.subject ?? '',
        content: updates.content ?? draft?.content ?? '',
        editor_state: updates.editor_state ?? draft?.editor_state ?? null,
        status: 'draft'
      }

      const { data, error } = await supabase
        .from('dispatch_drafts')
        .upsert(payload, { onConflict: 'team_id' })
        .select()
        .single()

      if (error) throw error
      setDraft(data)
      toast.success('Draft saved')
    } catch (err) {
      console.error('Error saving draft:', err)
      toast.error('Failed to save draft')
    } finally {
      setDraftSaving(false)
    }
  }

  const handleDraftSubjectChange = (value) => {
    setDraft((prev) => ({ ...prev, subject: value }))
  }

  const handleDraftContentChange = (value) => {
    setDraft((prev) => ({ ...prev, content: value }))
  }

  const handleClearDraft = async () => {
    if (!draft?.id) return
    
    const confirmed = await confirmDialog(
      'Discard the current draft?',
      { confirmText: 'Discard', cancelText: 'Keep Draft' }
    )
    if (!confirmed) return

    try {
      await supabase.from('dispatch_drafts').delete().eq('id', draft.id)
      setDraft(null)
      toast.success('Draft cleared')
    } catch (err) {
      console.error('Error clearing draft:', err)
      toast.error('Failed to clear draft')
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim()) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('draft_notes')
        .insert({
          team_id: team.id,
          content: newNote.trim()
        })

      if (error) throw error

      setNewNote('')
      await fetchNotes()
    } catch (err) {
      console.error('Error adding note:', err)
      toast.error('Failed to add note')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteNote = async (noteId) => {
    try {
      const { error } = await supabase
        .from('draft_notes')
        .delete()
        .eq('id', noteId)
        .eq('team_id', team.id)

      if (error) throw error
      await fetchNotes()
    } catch (err) {
      console.error('Error deleting note:', err)
      toast.error('Failed to delete note')
    }
  }

  const handleEditNote = (note) => {
    setEditingNoteId(note.id)
    setEditingContent(note.content)
  }

  const handleSaveEdit = async (noteId) => {
    try {
      const { error } = await supabase
        .from('draft_notes')
        .update({ content: editingContent })
        .eq('id', noteId)
        .eq('team_id', team.id)

      if (error) throw error
      setEditingNoteId(null)
      setEditingContent('')
      await fetchNotes()
      toast.success('Note updated')
    } catch (err) {
      console.error('Error updating note:', err)
      toast.error('Failed to update note')
    }
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditingContent('')
  }

  const toggleExpand = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }))
  }

  const handleClearAll = async () => {
    const confirmed = await confirmDialog(
      'Clear all draft notes?',
      { 
        confirmText: 'Clear All', 
        cancelText: 'Cancel',
        description: 'This action cannot be undone.'
      }
    )
    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('draft_notes')
        .delete()
        .eq('team_id', team.id)

      if (error) throw error
      setNotes([])
    } catch (err) {
      console.error('Error clearing notes:', err)
      toast.error('Failed to clear notes')
    }
  }

  const handleGenerateDispatch = async () => {
    // Warn if overwriting existing draft
    if (draft?.content && draft.content.trim()) {
      const confirmed = await confirmDialog(
        'You have an existing draft',
        {
          confirmText: 'Generate New',
          cancelText: 'Cancel',
          description: 'Generating a new dispatch will overwrite your current draft.'
        }
      )
      if (!confirmed) return
    }

    setGenerating(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-dispatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          teamId: team.id,
          userId: session.user.id,
          messageType,
          includeSchedule,
          specialInstructions: specialInstructions.trim() || undefined
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate dispatch')
      }

      navigate('/dispatch/review', {
        state: {
          generatedDispatch: result,
          teamId: team.id,
          draftId: draft?.id || null,
          // Preserve form state for return navigation
          formState: {
            messageType,
            includeSchedule,
            specialInstructions
          }
        }
      })
    } catch (err) {
      console.error('Error generating dispatch:', err)
      toast.error(err.message || 'Failed to generate dispatch. Please check your OpenAI API key.')
    } finally {
      setGenerating(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const day = date.toLocaleDateString('en-US', { weekday: 'long' })
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    return `${day} ${time}`
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold mb-1">
            Draft Notes
          </h2>
          <p className="text-sm text-brand-slate">
            Paste notes throughout the week, then generate your dispatch when ready
          </p>
        </div>
        {hasContent && (
          <div className="flex gap-2">
            {draft && (
              <Button
                variant="secondary"
                onClick={handleClearDraft}
                className="text-sm"
              >
                Clear Draft
              </Button>
            )}
            {hasNotes && (
              <Button
                variant="secondary"
                onClick={handleClearAll}
                className="text-sm"
              >
                Clear Notes
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleGenerateDispatch}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Dispatch'}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Only show Current Draft section if draft exists */}
        {(draft?.content || draft?.subject) && (
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">
              Current Draft
            </h3>
            {draftLoading ? (
              <div className="text-sm text-brand-slate">Loading draft...</div>
            ) : (
              <div className="space-y-4">
                <Textarea
                  label="Subject Line"
                  value={draft?.subject || ''}
                  onChange={(e) => handleDraftSubjectChange(e.target.value)}
                  placeholder="Weekly Team Update"
                />
                <DispatchEditor
                  content={draft?.content || ''}
                  onChange={handleDraftContentChange}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => handleSaveDraft({})}
                    disabled={draftSaving}
                  >
                    {draftSaving ? 'Saving...' : 'Save Draft'}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/dispatch/send', {
                      state: {
                        subjectLine: draft?.subject || '',
                        emailContent: draft?.content || '',
                        wikiItems: [],
                        teamId: team.id,
                        rawNotes: '',
                        draftId: draft?.id || null
                      }
                    })}
                  >
                    Send & Publish →
                  </Button>
                  <span className="text-sm text-brand-slate">
                    {!draft?.updated_at ? 'No saved draft yet' : `Last saved ${new Date(draft.updated_at).toLocaleString()}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <h3 className="font-heading text-lg font-semibold mb-3">
              Dispatch Options
            </h3>
            <div className="space-y-4 p-4 bg-white border border-border rounded-lg">
              <div>
                <label className="block font-medium text-brand-charcoal mb-2">
                  Message Type
                </label>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      id="message-type-weekly"
                      name="message-type"
                      value="weekly"
                      checked={messageType === 'weekly'}
                      onChange={(e) => setMessageType(e.target.value)}
                      className="mt-1 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                    />
                    <div className="flex-1">
                      <label htmlFor="message-type-weekly" className="font-medium text-brand-charcoal cursor-pointer">
                        Weekly Update
                      </label>
                      <p className="text-sm text-brand-slate mt-1">
                        Regular team update with schedule and week context
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      id="message-type-general"
                      name="message-type"
                      value="general"
                      checked={messageType === 'general'}
                      onChange={(e) => setMessageType(e.target.value)}
                      className="mt-1 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                    />
                    <div className="flex-1">
                      <label htmlFor="message-type-general" className="font-medium text-brand-charcoal cursor-pointer">
                        General Announcement
                      </label>
                      <p className="text-sm text-brand-slate mt-1">
                        Ad-hoc message without weekly schedule context
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {messageType === 'weekly' && (
                <div className="flex items-start gap-3 pt-2 border-t border-border">
                  <input
                    type="checkbox"
                    id="include-schedule"
                    checked={includeSchedule}
                    onChange={(e) => setIncludeSchedule(e.target.checked)}
                    className="mt-1 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <div className="flex-1">
                    <label htmlFor="include-schedule" className="font-medium text-brand-charcoal cursor-pointer">
                      Include Weekly Schedule
                    </label>
                    <p className="text-sm text-brand-slate mt-1">
                      Pull upcoming events from calendar and include them in the dispatch
                    </p>
                  </div>
                </div>
              )}
              
              <div className={messageType === 'weekly' ? '' : 'pt-2 border-t border-border'}>
                <label htmlFor="special-instructions" className="block font-medium text-brand-charcoal mb-2">
                  Special Instructions (Optional)
                </label>
                <Textarea
                  id="special-instructions"
                  placeholder="Add any special instructions for this dispatch... (e.g., 'Use a more urgent tone', 'Emphasize the fundraiser', 'Keep it brief')"
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-brand-slate mt-1">
                  These instructions will guide the AI when generating this specific dispatch
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg font-semibold mb-3">
              Raw Notes
            </h3>
            <Textarea
              placeholder="Paste your notes here... (e.g., Coach said practice moved to 6pm, Tournament hotel link, Picture day next Friday)"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={4}
              disabled={saving}
            />
            <div className="mt-2 flex justify-end">
              <Button
                variant="primary"
                onClick={handleAddNote}
                disabled={!newNote.trim() || saving}
              >
                {saving ? 'Adding...' : 'Add Note'}
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-brand-slate">
            Loading notes...
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
            <p className="text-brand-slate mb-2">
              No draft notes yet
            </p>
            <p className="text-sm text-brand-slate">
              Add your first note above to get started
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => {
              const isLong = note.content.length > 300
              const isExpanded = expandedNotes[note.id]
              const isEditing = editingNoteId === note.id
              
              return (
                <div
                  key={note.id}
                  className="p-4 bg-white border border-border rounded-lg"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-xs text-brand-slate mb-2">
                        {formatDate(note.created_at)}
                      </div>
                      {isEditing ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            rows={6}
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="primary"
                              onClick={() => handleSaveEdit(note.id)}
                              className="text-sm"
                            >
                              Save
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={handleCancelEdit}
                              className="text-sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div 
                            className={`text-brand-charcoal whitespace-pre-wrap ${
                              isLong && !isExpanded ? 'max-h-24 overflow-hidden relative' : ''
                            }`}
                          >
                            {note.content}
                            {isLong && !isExpanded && (
                              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
                            )}
                          </div>
                          {isLong && (
                            <button
                              onClick={() => toggleExpand(note.id)}
                              className="text-brand-green hover:text-brand-green-dark text-sm mt-2"
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditNote(note)}
                          className="text-brand-green hover:text-brand-green-dark text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Card>
  )
}

export default DraftNotes

